import React, { useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../firebase-config";
import { useNavigate } from "react-router-dom";
import { createProjectFromTemplate, getPackageOption, PACKAGE_OPTIONS } from "../../lib/projectTemplates";
import "../../assets/styles/AdminShared.css";

const NewProjectWizard = () => {
  const [projectName, setProjectName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("growth");
  const [startDate, setStartDate] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const selectedOption = getPackageOption(selectedPackage);

  const handleCreateProject = async () => {
    if (!projectName || !clientEmail || !startDate) {
      setMessage("Please fill in all fields, including the start date.");
      return;
    }

    setLoading(true);
    setMessage("");

    const projectSkeleton = createProjectFromTemplate({
      packageId: selectedPackage,
      projectName,
      clientEmail,
      startDate,
    });

    try {
      const docRef = await addDoc(collection(db, "projects"), projectSkeleton);
      navigate(`/admin/project/${docRef.id}`);
    } catch (error) {
      console.error("Error creating project:", error);
      setMessage("Failed to create project. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="ap-page ap-page--narrow">
      <div className="ap-page-header">
        <a href="/admin" className="ap-back">← Back to dashboard</a>
        <p className="ap-eyebrow">Admin</p>
        <h1 className="ap-title">New Project</h1>
        <p className="ap-subtitle">
          Create a package-aware client project with the right stages, actions, deliverables, and proposal defaults.
        </p>
      </div>

      <div className="ap-section" style={{ marginBottom: "20px" }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Select Package</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-pkg-toggle" style={{ flexWrap: "wrap" }}>
            {PACKAGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`ap-pkg-btn${selectedPackage === option.id ? " active" : ""}`}
                onClick={() => setSelectedPackage(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="ap-meta-row" style={{ marginTop: "18px" }}>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Investment</span>
              <span className="ap-meta-value">
                {selectedOption.price ? `$${selectedOption.price.toLocaleString()}` : "Custom quote"}
                {selectedOption.id === "care-plan" ? "/mo" : ""}
              </span>
            </div>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Timeline</span>
              <span className="ap-meta-value">{selectedOption.timeline}</span>
            </div>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Payment</span>
              <span className="ap-meta-value">{selectedOption.paymentPlan}</span>
            </div>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Revisions</span>
              <span className="ap-meta-value">
                {selectedOption.revisionLimit ? selectedOption.revisionLimit : "N/A"}
              </span>
            </div>
          </div>

          <p className="ap-pkg-desc">{selectedOption.description}</p>
        </div>
      </div>

      <div className="ap-section" style={{ marginBottom: "24px" }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Details</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="e.g., Liberty Roofing Website"
              required
            />
          </div>
          <div className="ap-form-group">
            <label>Client Email</label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="client@example.com"
              required
            />
          </div>
          <div className="ap-form-group">
            <label>Project Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div className="ap-btn-row">
        <button className="ap-btn-primary" onClick={handleCreateProject} disabled={loading}>
          {loading ? "Creating..." : `Create ${selectedOption.label} Project`}
        </button>
        {message && (
          <p className="ap-message--error" style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem", margin: 0 }}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default NewProjectWizard;
